import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

export function openContractsDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  let refreshInterval: ReturnType<typeof setInterval> | null = null;

  const dialog = new Dialog({
    title: 'Contracts',
    onClose: () => {
      if (refreshInterval) clearInterval(refreshInterval);
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:360px;max-height:450px;overflow-y:auto;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();

    // ─── Active Contracts ────────────────────────────────
    if (state.contracts.length > 0) {
      const title = document.createElement('div');
      title.textContent = 'Active Contracts';
      title.style.cssText = 'font-size:13px;font-weight:600;border-bottom:1px solid var(--border-color);padding-bottom:4px;';
      container.appendChild(title);

      for (const contract of state.contracts) {
        const card = document.createElement('div');
        card.className = 'card';
        card.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:4px;';

        // Top row: name + reward
        const top = document.createElement('div');
        top.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;';
        top.innerHTML = `<strong>${contract.clientName}</strong><span style="color:var(--color-success);font-family:var(--font-pixel);">${formatCurrency(contract.reward)}</span>`;
        card.appendChild(top);

        // Progress bar
        const bar = document.createElement('div');
        bar.className = 'progress-bar';
        bar.style.height = '6px';
        const fill = document.createElement('div');
        fill.className = 'progress-bar__fill';
        fill.style.cssText = `width:${Math.min(100, contract.progress)}%;background:var(--color-success);`;
        bar.appendChild(fill);
        card.appendChild(bar);

        // Deadline
        const ticksPerWeek = 70;
        const weeksLeft = Math.max(0, Math.ceil(contract.deadlineTicks / ticksPerWeek));
        const isUrgent = weeksLeft <= 2;
        const deadlineEl = document.createElement('div');
        deadlineEl.style.cssText = `font-size:10px;color:${isUrgent ? 'var(--color-danger)' : 'var(--color-text-muted)'};`;
        deadlineEl.textContent = `⏱ ${weeksLeft} weeks remaining · ${contract.requiredStat} · ${Math.round(contract.progress)}%`;
        if (isUrgent) deadlineEl.textContent += ' ⚠ URGENT';
        card.appendChild(deadlineEl);

        // Workers
        const workers = contract.assignedStaffIds
          .map((id) => state.staff.find((s) => s.id === id)?.name)
          .filter(Boolean);
        const isPaused = workers.length === 0;
        const workerEl = document.createElement('div');
        workerEl.style.cssText = `font-size:10px;color:${isPaused ? 'var(--color-warning)' : 'var(--color-text-secondary)'};`;
        workerEl.textContent = isPaused
          ? `⏸ Paused — no ${contract.assignmentMode === 'specific' ? 'assigned' : 'idle'} staff`
          : `Working: ${workers.join(', ')}`;
        card.appendChild(workerEl);

        // Mode + staff assignment controls
        const modeRow = document.createElement('div');
        modeRow.style.cssText = 'display:flex;gap:4px;align-items:center;margin-top:2px;';

        const idleBtn = document.createElement('button');
        idleBtn.className = `btn${contract.assignmentMode === 'idle' ? ' btn--active' : ''}`;
        idleBtn.textContent = 'Any Idle';
        idleBtn.style.cssText = 'font-size:9px;padding:2px 6px;min-height:20px;';
        idleBtn.addEventListener('click', () => {
          contract.assignmentMode = 'idle';
          contract.lockedStaffIds = [];
          render();
        });

        const specificBtn = document.createElement('button');
        specificBtn.className = `btn${contract.assignmentMode === 'specific' ? ' btn--active' : ''}`;
        specificBtn.textContent = 'Pick Staff';
        specificBtn.style.cssText = 'font-size:9px;padding:2px 6px;min-height:20px;';
        specificBtn.addEventListener('click', () => {
          contract.assignmentMode = 'specific';
          render();
        });

        modeRow.appendChild(idleBtn);
        modeRow.appendChild(specificBtn);
        card.appendChild(modeRow);

        // Staff picker (when in specific mode)
        if (contract.assignmentMode === 'specific') {
          const pickerEl = document.createElement('div');
          pickerEl.style.cssText = 'display:flex;flex-direction:column;gap:2px;margin-top:4px;';

          const allStaff = state.staff.filter((s) => !s.isTraining && !s.isOnLeave);
          const lockedSet = new Set(contract.lockedStaffIds);
          // Don't show staff locked to OTHER contracts
          const otherLocked = new Set(
            state.contracts.filter((c) => c.id !== contract.id).flatMap((c) => c.lockedStaffIds),
          );

          for (const staff of allStaff) {
            if (otherLocked.has(staff.id)) continue;
            const isAssigned = lockedSet.has(staff.id);
            const row = document.createElement('div');
            row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;font-size:10px;padding:1px 0;';

            const nameEl = document.createElement('span');
            nameEl.textContent = `${staff.name} (${staff.role} Lv.${staff.level})`;
            nameEl.style.color = isAssigned ? 'var(--color-success)' : 'var(--color-text-secondary)';

            const toggleBtn = document.createElement('button');
            toggleBtn.className = `btn${isAssigned ? ' btn--success' : ''}`;
            toggleBtn.textContent = isAssigned ? '✓' : '+';
            toggleBtn.style.cssText = 'font-size:9px;padding:1px 6px;min-height:18px;min-width:24px;';
            toggleBtn.addEventListener('click', () => {
              if (isAssigned) {
                contract.lockedStaffIds = contract.lockedStaffIds.filter((id) => id !== staff.id);
              } else {
                contract.lockedStaffIds.push(staff.id);
              }
              render();
            });

            row.appendChild(nameEl);
            row.appendChild(toggleBtn);
            pickerEl.appendChild(row);
          }
          card.appendChild(pickerEl);
        }

        // Penalty warning
        const penaltyEl = document.createElement('div');
        penaltyEl.style.cssText = 'font-size:9px;color:var(--color-text-muted);';
        penaltyEl.textContent = `Failure penalty: ${formatCurrency(contract.failurePenalty)}`;
        card.appendChild(penaltyEl);

        container.appendChild(card);
      }
    }

    // ─── Available Contracts ─────────────────────────────
    const availTitle = document.createElement('div');
    availTitle.textContent = `Available Contracts (${state.contractPool.length})`;
    availTitle.style.cssText = 'font-size:13px;font-weight:600;border-bottom:1px solid var(--border-color);padding-bottom:4px;margin-top:8px;';
    container.appendChild(availTitle);

    if (state.contractPool.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No contracts available right now.';
      empty.style.cssText = 'font-size:11px;color:var(--color-text-muted);padding:8px 0;text-align:center;';
      container.appendChild(empty);
    }

    for (const contract of state.contractPool) {
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:4px;';

      const top = document.createElement('div');
      top.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;';
      top.innerHTML = `<strong>${contract.clientName}</strong><span style="color:var(--color-success);font-family:var(--font-pixel);">${formatCurrency(contract.reward)}</span>`;
      card.appendChild(top);

      const info = document.createElement('div');
      info.style.cssText = 'font-size:10px;color:var(--color-text-secondary);';
      const weeksDeadline = Math.ceil(contract.deadlineTicks / 70);
      info.textContent = `${contract.requiredStat} · ${contract.requiredStaff} staff · ${Math.round(contract.durationTicks / 70)} wk est. · ⏱ ${weeksDeadline} wk limit`;
      card.appendChild(info);

      const penaltyInfo = document.createElement('div');
      penaltyInfo.style.cssText = 'font-size:9px;color:var(--color-text-muted);';
      penaltyInfo.textContent = `Failure penalty: ${formatCurrency(contract.failurePenalty)}`;
      card.appendChild(penaltyInfo);

      const hint = document.createElement('div');
      hint.style.cssText = 'font-size:9px;color:var(--color-text-muted);';
      hint.textContent = 'Idle staff auto-assigned. You can pick specific staff after accepting.';
      card.appendChild(hint);

      const acceptBtn = document.createElement('button');
      acceptBtn.className = 'btn btn--success';
      acceptBtn.textContent = 'Accept';
      acceptBtn.style.cssText = 'font-size:11px;align-self:flex-end;';
      acceptBtn.addEventListener('click', () => {
        const result = store.dispatch({ type: 'ACCEPT_CONTRACT', contractId: contract.id, staffIds: [] });
        if (result.success) render();
      });
      card.appendChild(acceptBtn);

      container.appendChild(card);
    }
  }

  render();
  refreshInterval = setInterval(() => render(), 1000);
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
